import { ChoiceBranchingDestination, ChoiceBranchingRule, ChoiceRiskRule } from "../models"

const nonNullDestinations = (destinations: (ChoiceBranchingDestination | null)[]) =>
    destinations.filter(
        (destination): destination is ChoiceBranchingDestination => destination !== null
    );

export const setDestination = (
    rule: ChoiceBranchingRule,
    choiceId: string,
    questionId: string
): ChoiceBranchingRule => {
    const destinations = nonNullDestinations(rule.destinations ?? []);
    const hasDestination = destinations.some(destination => destination.choiceId === choiceId);

    return {
        ...rule,
        destinations: hasDestination
                ? destinations.map(destination => destination.choiceId === choiceId
                ? { ...destination, destinationQuestionId: questionId }
                : destination)
            : [...destinations, { choiceId, destinationQuestionId: questionId }]
    };
}

export const removeDestination = (
    rule: ChoiceBranchingRule,
    choiceId: string
): ChoiceBranchingRule => ({
    ...rule,
    destinations: nonNullDestinations(rule.destinations ?? []).filter(
        destination => destination.choiceId !== choiceId
    )
});

export const renameDestination = (
    rule: ChoiceBranchingRule,
    oldChoiceId: string,
    newChoiceId: string
): ChoiceBranchingRule => {
    const destinations = nonNullDestinations(rule.destinations ?? []);
    if (oldChoiceId !== newChoiceId && destinations.some(destination => destination.choiceId === newChoiceId)) {
        return rule;
    }
    return {
        ...rule,
        destinations: destinations.map(destination => destination.choiceId === oldChoiceId
            ? { ...destination, choiceId: newChoiceId }
            : destination)
    };
};

export const updatedChoiceBranchList = (oldChoiceID: string, newChoiceID: string, [...expectedAnswers]: (string | null)[]) => {
    return expectedAnswers.map(expectedAnswer => expectedAnswer === oldChoiceID ? newChoiceID : expectedAnswer)
}

export const updatedChoiceBranchingRule = (oldChoiceID: string, newChoiceID: string, currentBranchingRule: ChoiceBranchingRule | null | undefined) => {
    if (!currentBranchingRule) return undefined;

    const destinations = nonNullDestinations(currentBranchingRule.destinations ?? []);
    const hasPerChoiceDestinations = currentBranchingRule.destinations !== undefined &&
        currentBranchingRule.destinations !== null;

    // Do not merge two per-choice routes if the new ID already belongs to
    // another choice. Keep the rule unchanged until the edit is corrected.
    const renamesIntoExistingDestination = hasPerChoiceDestinations &&
        oldChoiceID !== newChoiceID &&
        destinations.some(destination => destination.choiceId === newChoiceID);
    if (renamesIntoExistingDestination) return currentBranchingRule;

    let updatedRule: ChoiceBranchingRule = currentBranchingRule;
    const expectedAnswers = currentBranchingRule.expectedAnswerValues;

    // Legacy rules store choice IDs in expectedAnswerValues.
    if (expectedAnswers && expectedAnswers.length > 0) {
        updatedRule = {
            ...updatedRule,
            expectedAnswerValues: updatedChoiceBranchList(
                oldChoiceID,
                newChoiceID,
                expectedAnswers
            )
        };
    }

    // Per-choice rules store choice IDs as destinations[].choiceId.
    if (hasPerChoiceDestinations) {
        updatedRule = renameDestination(updatedRule, oldChoiceID, newChoiceID);
    }

    return updatedRule;
}

export const removeChoiceIDFromBranching = <T extends string | null>(
    choiceID: string,
    [...expectedAnswers]: T[]
) => {
    return expectedAnswers.filter(expectedAnswer => expectedAnswer !== choiceID)
}

export const removedChoiceIDBranchingRule = (choiceID: string, currentBranchingRule: ChoiceBranchingRule | null | undefined) => {
    // There is nothing to update when branching logic is not enabled.
    if (!currentBranchingRule)
        return undefined

    let newBranchingRule: ChoiceBranchingRule = currentBranchingRule
    const currentExpectedAnswers = currentBranchingRule.expectedAnswerValues

    // Legacy rules store selected choice IDs in expectedAnswerValues.
    if (currentExpectedAnswers && currentExpectedAnswers.length > 0){
        const newExpectedAnswers = removeChoiceIDFromBranching(choiceID, currentExpectedAnswers )
        newBranchingRule = {
            ...currentBranchingRule,
            expectedAnswerValues: newExpectedAnswers
        }
    }

    // Per-choice rules store the choice ID in destinations[].choiceId.
    if (currentBranchingRule.destinations !== undefined && currentBranchingRule.destinations !== null) {
        newBranchingRule = removeDestination(newBranchingRule, choiceID)
    }
    return newBranchingRule
}

export const removedChoiceIDRiskRule = (choiceID: string, currentRiskRule: ChoiceRiskRule | null | undefined) => {
    let newBranchingRule = currentRiskRule
    if (!currentRiskRule)
        return undefined
    const currentExpectedAnswers = currentRiskRule?.alertOn
    if (currentRiskRule && currentExpectedAnswers && currentExpectedAnswers.length > 0){
        const newExpectedAnswers = removeChoiceIDFromBranching(choiceID, currentExpectedAnswers )
        newBranchingRule = {
            ...currentRiskRule,
            alertOn: newExpectedAnswers
        }
    }
    return newBranchingRule
}